import React from "react";
import { Modal, Timeline, Tag, Spin } from "antd";
import { History } from "lucide-react";
import dayjs from "dayjs";
import { IStatusHistory, TStatusHistoryEntityType } from "../../types/api/statusHistory";
import { useStatusHistory } from "../../hooks/api/status-history/hooks";

const STATUS_COLOR: Record<string, string> = {
  // quotes
  draft: "default",
  sent: "blue",
  viewed: "geekblue",
  accepted: "green",
  rejected: "red",
  expired: "orange",
  converted: "purple",
  // invoices
  overdue: "red",
  written_off: "default",
  void: "default",
  // purchase orders
  acknowledged: "cyan",
  in_progress: "processing",
  partially_fulfilled: "gold",
  fulfilled: "green",
  cancelled: "error",
};

function statusTag(s: string | null) {
  if (!s) return <span className="text-slate-400 text-xs italic">—</span>;
  const color = STATUS_COLOR[s] ?? "default";
  return (
    <Tag color={color} className="text-xs">
      {s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
    </Tag>
  );
}

interface Props {
  open: boolean;
  entityType: TStatusHistoryEntityType;
  entityId: number;
  entityLabel?: string;
  onClose: () => void;
}

const StatusHistoryModal: React.FC<Props> = ({
  open,
  entityType,
  entityId,
  entityLabel,
  onClose,
}) => {
  const { data, isLoading } = useStatusHistory(
    open ? { entity_type: entityType, entity_id: entityId } : null,
  );

  const rows: IStatusHistory[] = (data?.data as IStatusHistory[] | undefined) ?? [];

  const entityTypeLabel =
    entityType === "purchase_order"
      ? "Purchase Order"
      : entityType === "quote"
        ? "Quotation"
        : "Invoice";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center gap-2">
          <History size={16} className="text-slate-500" />
          <span>
            Status History
            {entityLabel && (
              <span className="text-slate-400 font-normal text-sm ml-1.5">
                — {entityLabel}
              </span>
            )}
          </span>
        </div>
      }
      footer={null}
      width={520}
      destroyOnClose
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-slate-400 text-sm text-center py-6">
          No status changes recorded for this {entityTypeLabel.toLowerCase()}.
        </p>
      ) : (
        <Timeline
          className="mt-4"
          items={rows.map((row) => ({
            color:
              row.new_status === "void" || row.new_status === "cancelled" || row.new_status === "rejected"
                ? "red"
                : row.new_status === "accepted" || row.new_status === "fulfilled"
                  ? "green"
                  : "blue",
            children: (
              <div className="pb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {statusTag(row.previous_status)}
                  <span className="text-slate-400 text-xs">→</span>
                  {statusTag(row.new_status)}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  {dayjs(row.created_at).format("DD MMM YYYY HH:mm")}
                  {row.changed_by_name?.trim() && (
                    <span className="ml-2 text-slate-500">
                      by {row.changed_by_name.trim()}
                    </span>
                  )}
                  {!row.changed_by && (
                    <span className="ml-2 text-indigo-400 italic">client action</span>
                  )}
                </div>
                {row.notes && (
                  <p className="text-xs text-slate-500 mt-0.5 italic">{row.notes}</p>
                )}
              </div>
            ),
          }))}
        />
      )}
    </Modal>
  );
};

export default StatusHistoryModal;
